/** @jsx jsx */
import { ClassNames, jsx } from '@emotion/react';
import { AfterPage, AfterPageProps } from '@sprinx/after-razzle';
import { SupportedLocale } from '@sprinx/knihovna-types';
import articleData from 'api/articleData';
import Page from 'components/Page';
import MainLayout from 'layouts/MainLayout';
import ErrorPage from 'pages/ErrorPage';
import NotFoundPage from 'pages/NotFoundPage/NotFoundPage';
import { Article } from 'types/Article';
import { LocaleSwitchHrefs } from 'types/LocaleSwitchHrefs';
import { PageLayout } from 'types/PageLayout';
import { Product } from 'types/Product';
import { buildLocaleSwitchHrefs } from 'utils/buildLocaleSwitchHrefs';
import BrandPageBrands from './components/BrandPageBrands';

export interface BrandPageProps extends AfterPageProps<BrandPageRouteParams> {
  article?: {
    layout: PageLayout;
    products?: Product[];
    title?: string;
  };
  locale: SupportedLocale;
  localeHrefs?: LocaleSwitchHrefs;
}

// eslint-disable-next-line @typescript-eslint/no-empty-interface
interface BrandPageRouteParams extends Article {
  locale: SupportedLocale;
}

const BrandPage: AfterPage<BrandPageRouteParams, BrandPageProps> = ({ article, error, locale, localeHrefs }) => {
  if (error) return <ErrorPage error={error} />;
  if (!article) return <NotFoundPage />;

  const { layout } = article;

  return (
    <MainLayout locale={locale} localeHrefs={localeHrefs} {...layout}>
      <ClassNames>
        {({ theme }) => (
          <Page
            pageTitle={article.title}
            css={{
              padding: theme.spacing(1),
            }}
          >
            {article.products && <BrandPageBrands locale={locale} products={article.products} />}
          </Page>
        )}
      </ClassNames>
    </MainLayout>
  );
};

BrandPage.getInitialProps = async ({ req, match }) => {
  const { locale } = match.params;

  const article = req?.$article ? req.$article : await articleData.get({ locale, path: 'brand' });
  const localeHrefs = buildLocaleSwitchHrefs(({ locale, routeUrl }) =>
    routeUrl('brand', {
      locale,
      params: {
        locale,
      },
    }),
  );

  return {
    article,
    locale,
    localeHrefs,
  };
};

export default BrandPage;
